const PAGE_W = 595.28;
const PAGE_H = 841.89;
const MARGIN = 48;
const CONTENT_W = PAGE_W - MARGIN * 2;

const COLORS = {
  ink: { r: 0.07, g: 0.07, b: 0.07 },
  muted: { r: 0.38, g: 0.38, b: 0.38 },
  line: { r: 0.9, g: 0.9, b: 0.9 },
  accent: { r: 0.07, g: 0.07, b: 0.07 },
  success: { r: 0.1, g: 0.5, b: 0.22 },
};

/** pdf-lib WinAnsi fonts cannot encode narrow NBSP (U+202F), em-dash, accents, etc. */
export function pdfSafeText(value) {
  return String(value ?? '-')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\u202f/g, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[^\x20-\x7E]/g, '')
    .trim() || '-';
}

function money(value, currency = 'DZD') {
  if (value == null || Number.isNaN(Number(value))) return '-';
  const n = Number(value);
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${currency}`;
}

function packNameFromItem(item) {
  const raw = item?.value ?? item?.name ?? '';
  return String(raw).split('-')[0] || String(raw) || '-';
}

function billingFromItem(item, labels) {
  const raw = String(item?.value ?? item?.name ?? '');
  if (raw.includes('-an')) return labels.annual;
  if (raw.includes('-mois')) return labels.monthly;
  return labels.annual;
}

function deriveLineItem(cartOrder, invoiceMeta, labels) {
  if (invoiceMeta?.productName) {
    const typeLabel = invoiceMeta.productType === 'domain' ? labels.domainType : labels.packType;
    const periodLabel = invoiceMeta.billingPeriod === 'annual' ? labels.annual : labels.monthly;
    return {
      description: invoiceMeta.productName,
      type: typeLabel,
      period: invoiceMeta.productType === 'domain' ? labels.annual : periodLabel,
      amount: invoiceMeta.totalAmount,
    };
  }
  if (!cartOrder?.item) return null;
  return {
    description: packNameFromItem(cartOrder.item),
    type: labels.packType,
    period: billingFromItem(cartOrder.item, labels),
    amount: cartOrder.gross_total ?? cartOrder.net_total,
  };
}

function defaultLabels() {
  return {
    invoice: 'FACTURE',
    store: 'Boutique',
    product: 'Produit',
    type: 'Type',
    period: 'Periode',
    amount: 'Montant',
    promo: 'Code promo',
    subtotal: 'Sous-total',
    discount: 'Reduction',
    total: 'Total paye',
    payment: 'Paiement CIB',
    paid: 'Paye',
    signature: 'KIO - SARL NEO FINTECH',
    website: 'www.kiostore.net',
    packType: 'Pack',
    domainType: 'Domaine',
    monthly: 'Mensuel',
    annual: 'Annuel',
  };
}

/**
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} [opts.invoiceNumber]
 * @param {Array<[string,string]>} [opts.paymentRows]
 * @param {object} [opts.cartOrder]
 * @param {object} [opts.invoiceMeta]
 */
export async function buildCheckoutReceiptPdf({
  title,
  invoiceNumber = '',
  paymentRows = [],
  cartOrder = null,
  invoiceMeta = {},
}) {
  const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');
  const labels = { ...defaultLabels(), ...(invoiceMeta.labels || {}) };

  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([PAGE_W, PAGE_H]);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const c = (color) => rgb(color.r, color.g, color.b);

  let y = PAGE_H - MARGIN;

  const drawText = (text, x, yPos, size, fontRef, color = COLORS.ink) => {
    page.drawText(pdfSafeText(text), {
      x, y: yPos, size, font: fontRef, color: c(color),
    });
  };

  const drawRule = (yPos) => {
    page.drawLine({
      start: { x: MARGIN, y: yPos },
      end: { x: PAGE_W - MARGIN, y: yPos },
      thickness: 1,
      color: c(COLORS.line),
    });
  };

  // Header — KIO brand
  drawText('KIO', MARGIN, y, 28, bold);
  drawText(labels.invoice, PAGE_W - MARGIN - 80, y, 18, bold);
  drawText(`#${pdfSafeText(invoiceNumber || '—')}`, PAGE_W - MARGIN - 80, y - 18, 10, font, COLORS.muted);
  const issued = new Date().toLocaleDateString('en-GB');
  drawText(issued, PAGE_W - MARGIN - 80, y - 32, 9, font, COLORS.muted);
  y -= 44;
  drawRule(y);
  y -= 28;

  // Bill to
  if (invoiceMeta.customerName) {
    drawText(invoiceMeta.customerName, MARGIN, y, 12, bold);
    y -= 16;
  }
  if (invoiceMeta.customerEmail) {
    drawText(invoiceMeta.customerEmail, MARGIN, y, 10, font, COLORS.muted);
    y -= 14;
  }
  if (invoiceMeta.storeName) {
    drawText(`${labels.store}: ${invoiceMeta.storeName}`, MARGIN, y, 10, font, COLORS.muted);
    y -= 14;
  }
  y -= 10;
  drawRule(y);
  y -= 24;

  // Line items table header
  const colProduct = MARGIN;
  const colType = MARGIN + 180;
  const colPeriod = MARGIN + 260;
  const colAmount = PAGE_W - MARGIN - 70;

  drawText(labels.product, colProduct, y, 9, bold, COLORS.muted);
  drawText(labels.type, colType, y, 9, bold, COLORS.muted);
  drawText(labels.period, colPeriod, y, 9, bold, COLORS.muted);
  drawText(labels.amount, colAmount, y, 9, bold, COLORS.muted);
  y -= 8;
  drawRule(y);
  y -= 18;

  const lineItem = deriveLineItem(cartOrder, invoiceMeta, labels);
  const currency = invoiceMeta.currency || cartOrder?.gross_total_currency || 'DZD';

  if (lineItem) {
    drawText(lineItem.description, colProduct, y, 11, bold);
    drawText(lineItem.type, colType, y, 10, font);
    drawText(lineItem.period, colPeriod, y, 10, font);
    drawText(money(lineItem.amount, currency), colAmount, y, 10, bold);
    y -= 22;
  }

  if (invoiceMeta.promoCode) {
    drawText(`${labels.promo}: ${invoiceMeta.promoCode}`, colProduct, y, 10, font, COLORS.success);
    y -= 20;
  }

  y -= 8;
  drawRule(y);
  y -= 22;

  // Totals
  const subtotal = invoiceMeta.baseAmount ?? cartOrder?.net_total ?? lineItem?.amount;
  const discount = invoiceMeta.discountAmount
    ?? (cartOrder?.reduction_total != null ? Number(cartOrder.reduction_total) : null)
    ?? (subtotal != null && invoiceMeta.totalAmount != null && subtotal > invoiceMeta.totalAmount
      ? subtotal - invoiceMeta.totalAmount
      : null);
  const total = invoiceMeta.totalAmount ?? cartOrder?.gross_total ?? lineItem?.amount;

  const drawTotalRow = (label, value, boldRow = false) => {
    drawText(label, MARGIN, y, boldRow ? 12 : 10, boldRow ? bold : font, boldRow ? COLORS.ink : COLORS.muted);
    drawText(value, colAmount, y, boldRow ? 13 : 10, boldRow ? bold : font);
    y -= boldRow ? 22 : 18;
  };

  if (subtotal != null) drawTotalRow(labels.subtotal, money(subtotal, currency));
  if (discount != null && Number(discount) > 0) {
    drawTotalRow(labels.discount, `-${money(discount, currency)}`);
  }
  drawTotalRow(labels.total, money(total, currency), true);

  y -= 8;
  drawRule(y);
  y -= 24;

  // Payment details
  drawText(labels.payment, MARGIN, y, 11, bold);
  y -= 6;
  drawText(pdfSafeText(title), MARGIN, y - 14, 10, font, COLORS.muted);
  y -= 32;

  paymentRows.forEach(([label, value]) => {
    drawText(label, MARGIN, y, 9, font, COLORS.muted);
    const val = pdfSafeText(value);
    const maxW = CONTENT_W - 160;
    drawText(val.length > 42 ? `${val.slice(0, 42)}...` : val, MARGIN + 160, y, 9, bold);
    y -= 16;
  });

  // KIO signature footer
  const footerY = MARGIN + 48;
  drawRule(footerY + 36);
  const sigX = PAGE_W / 2 - 60;
  page.drawLine({
    start: { x: sigX, y: footerY + 20 },
    end: { x: sigX + 120, y: footerY + 20 },
    thickness: 0.5,
    color: c(COLORS.muted),
  });
  drawText(labels.signature, sigX, footerY + 4, 10, bold);
  drawText(labels.website, sigX + 8, footerY - 10, 9, font, COLORS.muted);
  drawText(labels.paid, sigX + 36, footerY + 24, 8, font, COLORS.success);

  return pdfDoc.save();
}

export function receiptPdfFileName(orderNumber) {
  if (orderNumber) {
    const safe = String(orderNumber).replace(/[^\w.-]+/g, '_');
    return `kio-facture-${safe}.pdf`;
  }
  return 'invoice.pdf';
}
